/**
 * Navigation queue — a swipe-deck expressed as router state.
 *
 * The queue is the FUTURE: `cards[0]` is where it points, visited cards are
 * removed, history holds the past. See QUEUE.md §4.
 *
 * INV-5: cards are distinct under the position-match key, and search
 * granularity is uniform across a queue (all cards declare `search` or none
 * do). Asserted in `commitQueue`, the single commit door.
 * INV-6: cold-load totality — a missing or invalid snapshot yields the empty
 * shape, never a throw.
 */
import type { ParamsRecord, SearchRecord } from './types'

/** A card is the route's params, optionally pinning the search it belongs to. */
export type QueueCard = ParamsRecord & { search?: SearchRecord }

export type QueueRecord<
  TCard extends QueueCard = QueueCard,
  TState = unknown,
> = {
  to: string
  cards: TCard[]
  state: TState
  prefetch: number
  /**
   * Distinguishes two decks over ONE route (§6): a workspace, an inbox folder,
   * a playlist. Birth resumes an existing queue only when the id matches, so
   * entering a different deck starts a new sitting instead of resuming the old
   * one. `useQueue()` still takes no arguments — only one queue per `to` is live.
   */
  id?: string
}

const STORAGE_PREFIX = 'queue:'

/** `search` is typed off the index signature; read it through one cast. */
export const cardSearch = (card: QueueCard): SearchRecord | undefined =>
  (card as { search?: SearchRecord }).search

export const cardParams = (card: QueueCard): ParamsRecord => {
  const params: ParamsRecord = {}
  for (const [key, value] of Object.entries(card)) {
    if (key === 'search') continue
    params[key] = value as string
  }
  return params
}

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableStringify(entry)).join(',')}]`
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, entry]) => entry !== undefined)
    .toSorted(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return `{${entries
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`)
    .join(',')}}`
}

/**
 * The position-match key (INV-5). Identity is BY VALUE: cards restored from
 * sessionStorage are fresh JSON objects, so reference identity would silently
 * remove nothing and duplicate the card.
 */
export const cardKey = (card: QueueCard): string => {
  const search = cardSearch(card)
  return `${stableStringify(cardParams(card))}|${
    search === undefined ? '' : stableStringify(search)
  }`
}

export const sameCard = (a: QueueCard | undefined, b: QueueCard | undefined) =>
  a !== undefined && b !== undefined && cardKey(a) === cardKey(b)

export function assertQueueInvariants(to: string, cards: QueueCard[]): void {
  const declaringSearch = cards.filter(
    (card) => cardSearch(card) !== undefined,
  ).length
  if (declaringSearch !== 0 && declaringSearch !== cards.length) {
    throw new Error(
      `INV-5: queue "${to}" mixes cards that declare \`search\` with cards that do not. ` +
        'Search granularity must be uniform, otherwise one location matches two distinct cards.',
    )
  }

  const seen = new Set<string>()
  for (const card of cards) {
    const key = cardKey(card)
    if (seen.has(key)) {
      throw new Error(
        `INV-5: queue "${to}" contains duplicate card ${key}. Cards must be distinct under the position-match key.`,
      )
    }
    seen.add(key)
  }
}

const isPlainCard = (value: unknown): value is QueueCard => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false
  }
  return Object.entries(value).every(
    ([key, entry]) =>
      typeof entry === 'string' ||
      (key === 'search' && typeof entry === 'object' && entry !== null),
  )
}

/** Snapshots written by an older deploy are dropped, never thrown on (INV-6). */
const parseSnapshot = (raw: string, to: string): QueueRecord | null => {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) return null
  const candidate = parsed as Partial<QueueRecord>
  if (candidate.to !== to) return null
  if (!Array.isArray(candidate.cards)) return null
  if (!candidate.cards.every((card) => isPlainCard(card))) return null
  if (typeof candidate.prefetch !== 'number') return null
  try {
    assertQueueInvariants(to, candidate.cards)
  } catch {
    return null
  }
  if (candidate.id !== undefined && typeof candidate.id !== 'string')
    return null
  return {
    to,
    cards: candidate.cards,
    state: candidate.state,
    prefetch: candidate.prefetch,
    id: candidate.id,
  }
}

const getStorage = (): Storage | null => {
  try {
    return globalThis.sessionStorage
  } catch {
    return null
  }
}

export type QueueStore = {
  get: (to: string) => QueueRecord | undefined
  commit: (record: QueueRecord) => void
  subscribe: (listener: () => void) => () => void
}

/**
 * One store per RouterProvider. Hydrated at construction — not on first
 * `useQueue()`, since prefetch runs before any component mounts (§4.6).
 */
export function createQueueStore(): QueueStore {
  const records = new Map<string, QueueRecord>()
  const listeners = new Set<() => void>()

  const storage = getStorage()
  if (storage) {
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index)
      if (!key?.startsWith(STORAGE_PREFIX)) continue
      const raw = storage.getItem(key)
      if (raw === null) continue
      const to = key.slice(STORAGE_PREFIX.length)
      const record = parseSnapshot(raw, to)
      if (record) records.set(to, record)
    }
  }

  return {
    get: (to) => records.get(to),
    commit: (record) => {
      assertQueueInvariants(record.to, record.cards)
      records.set(record.to, record)
      storage?.setItem(`${STORAGE_PREFIX}${record.to}`, JSON.stringify(record))
      for (const listener of listeners) listener()
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
